import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, Alert, Stack } from "@mui/material";
import { Html5Qrcode } from "html5-qrcode";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { auth } from "../../firebase-config";

const CHECKIN_CF_URL =
  process.env.REACT_APP_CHECKIN_ATTENDEE_URL ||
  "https://checkinrsvp-774073159618.us-central1.run.app";

function parseQrPayload(text) {
  // Supports:
  // 1) full URL: https://.../checkin?org=IRI&event=EVT2025&rsvp=...&code=...
  // 2) query-only: org=...&event=...
  // 3) raw code formats (optional to add later)
  try {
    const t = String(text || "").trim();
    if (!t) return null;

    const url = t.startsWith("http") ? new URL(t) : new URL(`https://x.local/?${t}`);
    const org = url.searchParams.get("org");
    const event = url.searchParams.get("event");
    const rsvp = url.searchParams.get("rsvp");
    const code = url.searchParams.get("code");

    if (!org || !event || !rsvp || !code) return null;

    return { orgId: org, eventId: event, rsvpId: rsvp, code };
  } catch {
    return null;
  }
}

export default function CheckinScan() {
  const { orgId, eventId } = useParams();
  const navigate = useNavigate();

  const readerId = useMemo(() => `qr-reader-${orgId}-${eventId}`, [orgId, eventId]);
  const qrRef = useRef(null);

  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);

  const stopScanner = async () => {
    try {
      if (qrRef.current) {
        await qrRef.current.stop();
        await qrRef.current.clear();
      }
    } catch {
      // ignore
    } finally {
      qrRef.current = null;
      setRunning(false);
    }
  };

  const startScanner = async () => {
    setError(null);

    // Recreate instance each start to avoid mobile camera lock issues
    const qr = new Html5Qrcode(readerId);
    qrRef.current = qr;

    try {
      setRunning(true);

      await qr.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: { width: 280, height: 280 } },
        async (decodedText) => {
          if (busy) return;

          const parsed = parseQrPayload(decodedText);
          if (!parsed) {
            setLastResult({
              ok: false,
              title: "Invalid QR",
              detail: "QR did not contain org/event/rsvp/code.",
            });
            return;
          }

          // Ensure scanned code matches the console event route
          if (parsed.orgId !== orgId || parsed.eventId !== eventId) {
            setLastResult({
              ok: false,
              title: "Wrong event",
              detail: `This QR is for ${parsed.orgId}/${parsed.eventId}, not ${orgId}/${eventId}.`,
            });
            return;
          }

          setBusy(true);
          setLastResult(null);

          try {
            if (!auth.currentUser) {
              throw new Error("Not logged in. Please log in again.");
            }

            const idToken = await auth.currentUser.getIdToken(true);

            // Adjust payload to match your existing check-in CF contract
            const { data } = await axios.post(
              CHECKIN_CF_URL,
              {
                orgId,
                eventId,
                rsvpId,
                code,
                action: "arrival" // or omit action to auto-advance
              },
              {
                headers: {
                  Authorization: `Bearer ${idToken}`,
                  "organization-id": orgId,
                },
              }
            );

            setLastResult({
              ok: true,
              title: "Checked in",
              detail: data?.message || `RSVP ${parsed.rsvpId} updated.`,
            });
          } catch (e) {
            const msg =
              e?.response?.data?.error ||
              e?.response?.data?.message ||
              e?.message ||
              "Check-in failed.";
            setLastResult({ ok: false, title: "Check-in failed", detail: msg });
          } finally {
            setBusy(false);
          }
        },
        () => {
          // decode error callback: ignore (this fires constantly while scanning)
        }
      );
    } catch (e) {
      setError(e?.message || "Could not start camera.");
      await stopScanner();
    }
  };

  useEffect(() => {
    startScanner();
    return () => {
      stopScanner();
    };
  }, [readerId]);

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Scan QR codes
        </Typography>

        <Button variant="outlined" onClick={() => navigate(`/checkin-console/${orgId}/${eventId}/status`)}>
          View status
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {lastResult && (
        <Alert severity={lastResult.ok ? "success" : "warning"} sx={{ mb: 2 }}>
          <strong>{lastResult.title}</strong>
          <div>{lastResult.detail}</div>
        </Alert>
      )}

      <Box
        sx={{
          bgcolor: "white",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
          <Typography variant="body2" color="text.secondary">
            Camera scanner {running ? "(running)" : "(stopped)"}
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <div id={readerId} style={{ width: "100%" }} />
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={startScanner} disabled={running}>
          Start
        </Button>
        <Button variant="outlined" onClick={stopScanner} disabled={!running}>
          Stop
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        If the camera won’t start on iPhone, ensure Safari has camera permission for this site.
      </Typography>
    </Box>
  );
}